import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../../config/api'
import { csrfHeader } from '../../lib/csrf'
import { C } from '../../theme'

interface AMLMatch {
  listed_name: string
  list_source: string
  score: number
  match_type: string
}

interface AMLResult {
  risk_level: 'clear' | 'potential_match' | 'confirmed_match'
  match_found: boolean
  matches: AMLMatch[]
  lists_checked: string[]
  screened_name: string
  screened_dob: string | null
  screened_at: string
}

interface TestLabResponse {
  success: boolean
  mode: 'test'
  providers: string[]
  result: AMLResult
}

interface TestLabSectionProps {
  token: string
  newFullKey: string | null
  onUnauthorized: () => void
}

export function TestLabSection({ token, newFullKey, onUnauthorized }: TestLabSectionProps) {
  const navigate = useNavigate()
  const [fullName, setFullName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [nationality, setNationality] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [screening, setScreening] = useState<TestLabResponse | null>(null)

  const authHeaders: Record<string, string> = token === 'session'
    ? {}
    : { Authorization: `Bearer ${token}` }

  const openIdentityTest = () => {
    if (newFullKey) {
      sessionStorage.setItem('payrail-kyc:test-api-key', newFullKey)
    }
    navigate('/demo')
  }

  const runAML = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    setError('')
    setScreening(null)

    try {
      const response = await fetch(`${API_BASE_URL}/api/developer/test-lab/aml`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...csrfHeader(),
        },
        body: JSON.stringify({
          full_name: fullName,
          date_of_birth: dateOfBirth || null,
          nationality: nationality || null,
        }),
      })

      if (response.status === 401) {
        onUnauthorized()
        return
      }

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.message || payload.error || 'AML screening failed')
      }
      setScreening(payload)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'AML screening failed')
    } finally {
      setLoading(false)
    }
  }

  const riskColor = screening?.result.risk_level === 'clear'
    ? C.green
    : screening?.result.risk_level === 'potential_match'
      ? C.amber
      : C.red

  return (
    <section>
      <div className="section-title">
        <h2>Test Lab</h2>
        <span className="meta">identity · AML</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 8 }}>Identity check</div>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: '0 0 18px' }}>
            Run the hosted document, selfie, liveness and face-match flow. Use only documents you are authorized to submit.
          </p>
          {!newFullKey && (
            <p style={{ color: C.amber, fontSize: 12, lineHeight: 1.5, margin: '0 0 14px' }}>
              Have an API key ready. A newly created key is transferred to the test page for this browser session only.
            </p>
          )}
          <button type="button" className="btn primary" onClick={openIdentityTest}>
            Open identity test →
          </button>
        </div>

        <form onSubmit={runAML} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 8 }}>AML screening</div>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: '0 0 16px' }}>
            Screen a person against the watchlists configured on this deployment. Test requests are not added to verification metrics.
          </p>
          <label style={{ display: 'block', color: C.muted, fontSize: 12, marginBottom: 5 }} htmlFor="aml-full-name">Full legal name</label>
          <input
            id="aml-full-name"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            required
            minLength={2}
            maxLength={200}
            autoComplete="off"
            placeholder="e.g. John Smith"
            style={{ width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text, border: `1px solid ${C.borderStrong}`, borderRadius: 7, padding: '10px 12px', fontSize: 14, marginBottom: 12 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: 10 }}>
            <div>
              <label style={{ display: 'block', color: C.muted, fontSize: 12, marginBottom: 5 }} htmlFor="aml-dob">Date of birth</label>
              <input id="aml-dob" type="date" value={dateOfBirth} onChange={(event) => setDateOfBirth(event.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text, border: `1px solid ${C.borderStrong}`, borderRadius: 7, padding: '9px 10px', fontSize: 13 }} />
            </div>
            <div>
              <label style={{ display: 'block', color: C.muted, fontSize: 12, marginBottom: 5 }} htmlFor="aml-nationality">Nationality</label>
              <input id="aml-nationality" value={nationality} onChange={(event) => setNationality(event.target.value.replace(/[^a-z]/gi, '').slice(0, 2).toUpperCase())} placeholder="GB" maxLength={2} autoComplete="off" style={{ width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text, border: `1px solid ${C.borderStrong}`, borderRadius: 7, padding: '10px 12px', fontSize: 13, textTransform: 'uppercase' }} />
            </div>
          </div>
          <button type="submit" className="btn primary" disabled={loading || fullName.trim().length < 2} style={{ marginTop: 16 }}>
            {loading ? 'Screening…' : 'Run AML screening'}
          </button>

          {error && <div role="alert" style={{ marginTop: 14, padding: 12, background: C.redDim, color: C.red, border: `1px solid ${C.red}44`, borderRadius: 7, fontSize: 13 }}>{error}</div>}

          {screening && (
            <div aria-live="polite" style={{ marginTop: 16, padding: 14, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: C.muted, fontSize: 12 }}>Decision</span>
                <strong style={{ color: riskColor, fontFamily: C.mono, fontSize: 12, textTransform: 'uppercase' }}>{screening.result.risk_level.replace('_', ' ')}</strong>
              </div>
              <div style={{ marginTop: 8, color: C.muted, fontSize: 12 }}>
                Providers: {screening.providers.join(', ')} · Lists: {screening.result.lists_checked.join(', ')}
              </div>
              {screening.result.matches.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {screening.result.matches.slice(0, 5).map((match, index) => (
                    <div key={`${match.listed_name}-${match.list_source}-${index}`} style={{ borderTop: `1px solid ${C.border}`, paddingTop: 9, marginTop: 9, color: C.text, fontSize: 12 }}>
                      <strong>{match.listed_name}</strong>
                      <div style={{ color: C.muted, marginTop: 3 }}>{match.list_source} · {(match.score * 100).toFixed(1)}% · {match.match_type}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </section>
  )
}
